/**
 * 데일리 브리핑 노출 게이트 — 판정 규칙.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 파일을 따로 뒀나
 * ─────────────────────────────────────────────────────────────
 * navGate.ts / opsBriefingMockMode.ts 와 같은 이유입니다.
 * "이 센터에 기능을 열어도 되는가" 하나가 사고의 크기를 가릅니다.
 * 훅 안에 두면 react-query 를 띄워야만 확인할 수 있어서,
 * 규칙만 떼어 내고 테스트를 붙였습니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 반드시 fail-closed 입니다
 * ─────────────────────────────────────────────────────────────
 * 이 게이트는 **조용히 열리면** 사고가 납니다. 잘못 열리면 판정이 검증되기 전의
 * 이탈 위험 명단이 원장에게 보이고, 원장이 멀쩡한 회원에게 전화를 겁니다.
 * 한 번 그러면 기능 신뢰가 회복되지 않습니다.
 *
 * 그래서 "모르면 닫는다"입니다.
 *   · 아직 답을 못 받았다(로딩)  → 열지 않는다. 판정 전(isResolving) 으로 본다.
 *   · 조회가 실패했다            → 닫는다. 서버가 죽었다고 열어주면 안 된다.
 *   · enabled 가 true 가 아니다  → 닫는다. truthy 검사가 아니라 `=== true` 다.
 *
 * 잠깐이라도 열렸다가 닫히면 안 됩니다. 그래서 '로딩'과 '닫힘'을 구분합니다 —
 * 로딩 중에 곧바로 리다이렉트해 버리면, 열려 있는 센터의 원장이 매번
 * 대시보드로 튕겨나갑니다. 둘 다 "열지 않는다"는 같지만 화면 처리가 다릅니다.
 */

/** 판정에 쓰는 조회 상태 — react-query 결과에서 필요한 것만 추린 모양 */
export type AccessQueryState = {
    /** 서버 응답. 아직 못 받았으면 undefined */
    data: { enabled: boolean } | undefined;
    /** 재시도까지 소진하고 최종 실패했는가 */
    isError: boolean;
};

export type AccessDecision = {
    /** 화면을 열어도 되는가. 확정으로 열렸을 때만 true */
    allowed: boolean;
    /** 아직 판정 전인가. true 면 열지도 닫지도 말고 기다린다 */
    isResolving: boolean;
};

/**
 * 열어도 되는지 판정한다.
 *
 * @param query    access 조회 상태
 * @param canAsk   물어볼 수 있는 상태인가 (토큰·센터가 있는가).
 *                 물어볼 수조차 없으면 영원히 로딩으로 두지 않고 그냥 닫는다 —
 *                 계속 도는 스피너는 사용자에게 닫힌 것보다 나쁘다.
 */
export const decideOpsBriefingAccess = (
    query: AccessQueryState,
    canAsk: boolean
): AccessDecision => {
    // 물어볼 수 없는 상태 — 판정을 기다릴 것이 없다. 닫는다.
    if (!canAsk) return { allowed: false, isResolving: false };

    // 답이 왔거나 최종 실패했으면 판정이 끝난 것이다.
    const settled = query.isError || query.data !== undefined;
    if (!settled) return { allowed: false, isResolving: true };

    /*
     * `=== true` 로 비교한다.
     * truthy 검사면 서버가 enabled: 'false' 같은 문자열을 주는 순간 열린다.
     */
    return { allowed: query.data?.enabled === true, isResolving: false };
};
